import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Ticket as TicketIcon } from "lucide-react";

import { baseUrl } from "../../constant/constant";
import {
    setPurchasedTickets,
    showPurchasedTicket,
} from "../../stores/slices/ticketSlice";
import { showError } from "../../stores/slices/commonSlice";
import { OrderCard, InlineTicketView } from "./ticketCards";

const getViewerTickets = (order) =>
    (order?.stubs || []).map((stub) => ({
        ...stub,
        // Normalise the event exactly as My Tickets does before handing it to
        // the shared ticket viewers. /ticket-lookup returns the street address
        // as `address`, while the viewers (and the printable ticket) read
        // `venueaddress` — the name My Tickets adapts to. Passing the raw
        // payload through left that key undefined, so the location row was
        // simply not rendered on a looked-up ticket.
        event: {
            ...(order?.event || {}),
            venueaddress:
                order?.event?.venueaddress || order?.event?.address || "",
        },
        eventname:
            order.eventname || order.event?.title || order.event?.name || "",
    }));

/* =========================================================================
 *  Ticket Lookup result page (GET /lookup/:id).
 *
 *  Same split-pane layout as My Tickets: left rail with the order card
 *  (cover image, title overlay, ticket-count chip, date + venue) and a
 *  right rail with the inline detail view. There is only one card —
 *  this page is scoped to a single confirmation id — but rendering it
 *  as a list keeps the visual identical and lets us swap to multi-order
 *  results in the future without touching the page shell.
 *
 *  Mobile: list-only, the "View Tickets" button on the card opens the
 *  global PurchasedTicketModal. We also auto-open it on first load so
 *  the user lands on the QR without an extra tap.
 * ========================================================================= */
export default function LookupView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // The lookup form may pass the already-fetched order via router state so
    // we don't refetch (it showed the loading state on its button). Direct
    // visits / refreshes have no state and fall through to the fetch below.
    const prefetchedRef = useRef(location.state?.order ?? null);

    const [order, setOrder] = useState(prefetchedRef.current);
    const [stubIndex, setStubIndex] = useState(0);
    const [loading, setLoading] = useState(!prefetchedRef.current);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        // Consume a prefetched order once (on the navigation that carried it),
        // then clear it so a later id change refetches normally.
        if (prefetchedRef.current) {
            const o = prefetchedRef.current;
            prefetchedRef.current = null;
            setOrder(o);
            setStubIndex(0);
            setLoading(false);
            if (
                typeof window !== "undefined" &&
                window.innerWidth < 1024 &&
                o?.stubs?.length
            ) {
                dispatch(setPurchasedTickets(getViewerTickets(o)));
                dispatch(showPurchasedTicket(true));
            }
            return;
        }

        const fetchOrder = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(
                    `${baseUrl}/ticket-lookup/${encodeURIComponent(id)}`
                );
                const json = await res.json();
                if (cancelled) return;
                if (!res.ok) {
                    throw new Error(json?.message || "Ticket not found.");
                }
                setOrder(json.data || null);
                setStubIndex(0);

                // Mobile auto-open: surface the QR immediately. Desktop
                // users see the same data inline so no modal is needed.
                if (
                    typeof window !== "undefined" &&
                    window.innerWidth < 1024 &&
                    json.data?.stubs?.length
                ) {
                    dispatch(
                        setPurchasedTickets(getViewerTickets(json.data))
                    );
                    dispatch(showPurchasedTicket(true));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                    dispatch(showError(err.message));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchOrder();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const openMobileViewer = (o) => {
        dispatch(setPurchasedTickets(getViewerTickets(o)));
        dispatch(showPurchasedTicket(true));
    };

    if (loading) {
        // Skeleton mirroring the result layout (header + left order card +
        // right detail panel) so the page doesn't jump when the order loads.
        return (
            <div className="min-h-[calc(100vh-80px)] bg-gray-50 py-12 animate-pulse">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 pb-6 border-b border-gray-200">
                        <div className="h-9 w-56 bg-gray-200 rounded-lg mb-2 sm:mb-0" />
                        <div className="h-5 w-28 bg-gray-200 rounded" />
                    </div>

                    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
                        {/* Left: order card */}
                        <div className="w-full lg:w-[40%] flex flex-col space-y-6">
                            <div className="h-4 w-28 bg-gray-200 rounded" />
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="aspect-[2/1] bg-gray-200 w-full" />
                                <div className="p-6 space-y-4">
                                    <div className="h-6 w-3/4 bg-gray-200 rounded" />
                                    <div className="h-4 w-1/2 bg-gray-200 rounded" />
                                    <div className="h-px bg-gray-100 w-full" />
                                    <div className="space-y-3">
                                        <div className="h-4 w-2/3 bg-gray-200 rounded" />
                                        <div className="h-4 w-1/2 bg-gray-200 rounded" />
                                    </div>
                                    <div className="h-11 w-full bg-gray-200 rounded-xl mt-2" />
                                </div>
                            </div>
                        </div>

                        {/* Right: inline detail (desktop only) */}
                        <div className="hidden lg:block w-[60%] sticky top-28">
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 space-y-6">
                                <div className="h-7 w-1/2 bg-gray-200 rounded" />
                                <div className="aspect-[16/9] bg-gray-200 rounded-xl w-full" />
                                <div className="flex justify-center">
                                    <div className="h-40 w-40 bg-gray-200 rounded-xl" />
                                </div>
                                <div className="space-y-3">
                                    <div className="h-4 w-2/3 bg-gray-200 rounded" />
                                    <div className="h-4 w-1/2 bg-gray-200 rounded" />
                                    <div className="h-4 w-3/4 bg-gray-200 rounded" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="min-h-[calc(100vh-80px)] bg-gray-50 flex flex-col items-center justify-center px-4">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <TicketIcon size={40} className="text-red-400" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-3">
                    Ticket not found
                </h2>
                <p className="text-gray-600 font-medium mb-6 text-center max-w-md">
                    We couldn't find a ticket matching{" "}
                    <span className="font-mono font-black">{id}</span>. Double-check
                    the confirmation id from your email, or use the "Resend to Email"
                    link if you've lost it.
                </p>
                <button
                    type="button"
                    onClick={() => navigate("/lookup")}
                    className="bg-[#ffea00] hover:bg-[#e6d300] text-black font-black px-6 py-3 rounded-xl transition-all shadow-md flex items-center"
                >
                    <ArrowLeft size={18} className="mr-2" /> Back to Lookup
                </button>
            </div>
        );
    }

    const eventTitle =
        order?.eventname || order?.event?.title || order?.event?.name || "";

    return (
        <div className="min-h-[calc(100vh-80px)] bg-gray-50 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Header + back link */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 pb-6 border-b border-gray-200">
                    <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-2 sm:mb-0">
                        {eventTitle}
                    </h2>
                    <button
                        type="button"
                        onClick={() => navigate("/lookup")}
                        className="flex items-center text-gray-500 hover:text-black font-bold transition-colors group"
                    >
                        <ArrowLeft
                            size={18}
                            className="mr-2 group-hover:-translate-x-1 transition-transform"
                        />
                        Back to Lookup
                    </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">

                    {/* ============== LEFT: single order card ============== */}
                    <div className="w-full lg:w-[40%] flex flex-col space-y-6">
                        <p className="text-sm font-black text-gray-400 tracking-widest uppercase">
                            Confirmation
                        </p>
                        <OrderCard
                            order={order}
                            isSelected={true}
                            onSelect={() => {}}
                            onOpenViewer={openMobileViewer}
                        />
                    </div>

                    {/* ============== RIGHT: inline detail (desktop) ============== */}
                    <div className="hidden lg:block w-[60%] sticky top-28 animate-pop-in">
                        <InlineTicketView
                            order={order}
                            stubIndex={stubIndex}
                            setStubIndex={setStubIndex}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
